/* Inline SVG icons — stroke 1.6, 18px default. */
import React from 'react';

export interface IconProps
  extends Omit<React.SVGProps<SVGSVGElement>, 'stroke' | 'fill' | 'children' | 'd'> {
  /** A path string, or arbitrary SVG children for multi-shape icons. */
  d?: string | React.ReactNode;
  viewBox?: string;
  size?: number;
  stroke?: number;
  fill?: string;
}

export const Icon = ({
  d,
  viewBox = '0 0 24 24',
  size = 18,
  stroke = 1.6,
  fill = 'none',
  ...p
}: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill={fill}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...p}
  >
    {typeof d === 'string' ? <path d={d} /> : d}
  </svg>
);

export type IconElement = React.ReactElement<IconProps>;

/** Re-render an icon at a different size / stroke weight. */
export const sized = (el: IconElement, size: number, stroke?: number): IconElement =>
  React.cloneElement(el, stroke == null ? { size } : { size, stroke });

export const ic = {
  dashboard: <Icon d={<><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></>}/>,
  org:       <Icon d={<><path d="M3 21h18"/><rect x="5" y="6" width="6" height="15" rx="1"/><rect x="13" y="9" width="6" height="12" rx="1"/><path d="M7 10h2M7 13h2M7 16h2M15 13h2M15 16h2"/></>}/>,
  site:      <Icon d={<><path d="M3 10l9-6 9 6"/><path d="M5 9v12h14V9"/><path d="M10 21v-6h4v6"/></>}/>,
  instr:     <Icon d={<><circle cx="12" cy="8" r="3.6"/><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7"/></>}/>,
  users:     <Icon d={<><circle cx="9" cy="8" r="3.2"/><circle cx="17" cy="9" r="2.6"/><path d="M2.5 20c0-3.6 3-5.6 6.5-5.6s6.5 2 6.5 5.6"/><path d="M21.5 20c0-2.8-2-4.5-4.5-4.5"/></>}/>,
  admin:     <Icon d={<><circle cx="12" cy="12" r="3"/><path d="M19.4 13a7.5 7.5 0 0 0 0-2l2-1.5-2-3.4-2.3.8a7.6 7.6 0 0 0-1.7-1L15 3h-4l-.4 2.4a7.6 7.6 0 0 0-1.7 1l-2.3-.8-2 3.4 2 1.5a7.5 7.5 0 0 0 0 2l-2 1.5 2 3.4 2.3-.8a7.6 7.6 0 0 0 1.7 1L11 21h4l.4-2.4a7.6 7.6 0 0 0 1.7-1l2.3.8 2-3.4z"/></>}/>,
  search:    <Icon d={<><circle cx="11" cy="11" r="6.5"/><path d="M20 20l-3.5-3.5"/></>}/>,
  filter:    <Icon d="M3 5h18l-7 9v6l-4-2v-4z"/>,
  chevD:     <Icon d="M6 9l6 6 6-6"/>,
  chevR:     <Icon d="M9 6l6 6-6 6"/>,
  chevL:     <Icon d="M15 6l-6 6 6 6"/>,
  chevU:     <Icon d="M6 15l6-6 6 6"/>,
  edit:      <Icon d={<><path d="M4 20l4-1 11-11-3-3L5 16z"/><path d="M14 6l3 3"/></>}/>,
  save:      <Icon d={<><path d="M5 5h11l3 3v11H5z"/><path d="M8 5v5h7V5M8 19v-6h8v6"/></>}/>,
  plus:      <Icon d="M12 5v14M5 12h14"/>,
  close:     <Icon d="M6 6l12 12M18 6L6 18"/>,
  check:     <Icon d="M4 12l5 5L20 6"/>,
  warn:      <Icon d={<><path d="M12 4l10 17H2z"/><path d="M12 10v5M12 18.5v.5"/></>}/>,
  info:      <Icon d={<><circle cx="12" cy="12" r="9"/><path d="M12 8v.5M11 12h1v5h1"/></>}/>,
  lock:      <Icon d={<><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></>}/>,
  eye:       <Icon d={<><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></>}/>,
  /** El mismo ojo, tachado: «ocultar lo que ahora se ve». */
  eyeOff:    <Icon d={<><path d="M2 12s3.5-7 10-7c1.7 0 3.2.5 4.5 1.2M22 12s-3.5 7-10 7c-1.7 0-3.2-.5-4.5-1.2"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M3 3l18 18"/></>}/>,
  upload:    <Icon d={<><path d="M12 4v12"/><path d="M7 9l5-5 5 5"/><path d="M4 20h16"/></>}/>,
  download:  <Icon d={<><path d="M12 4v12"/><path d="M7 11l5 5 5-5"/><path d="M4 20h16"/></>}/>,
  excel:     <Icon d={<><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 9l6 6M15 9l-6 6"/></>}/>,
  globe:     <Icon d={<><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></>}/>,
  bell:      <Icon d={<><path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8z"/><path d="M10 21a2 2 0 0 0 4 0"/></>}/>,
  star:      <Icon d="M12 3l3 6.5 7 1-5 5 1 7-6-3.5L6 22.5l1-7-5-5 7-1z"/>,
  sun:       <Icon d={<><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></>}/>,
  flag:      <Icon d={<><path d="M5 21V4"/><path d="M5 5h13l-2 4 2 4H5"/></>}/>,
  pin:       <Icon d={<><path d="M12 22s7-7 7-12a7 7 0 0 0-14 0c0 5 7 12 7 12z"/><circle cx="12" cy="10" r="2.5"/></>}/>,
  refresh:   <Icon d={<><path d="M20 11a8 8 0 0 0-14-5l-2 2"/><path d="M4 13a8 8 0 0 0 14 5l2-2"/><path d="M4 3v5h5M20 21v-5h-5"/></>}/>,
  more:      <Icon d={<><circle cx="5"  cy="12" r="1.4"/><circle cx="12" cy="12" r="1.4"/><circle cx="19" cy="12" r="1.4"/></>}/>,
  trash:     <Icon d={<><path d="M4 7h16M9 7V4h6v3"/><path d="M6 7l1 13h10l1-13"/></>}/>,
  user:      <Icon d={<><circle cx="12" cy="8" r="3.6"/><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7"/></>}/>,
  mail:      <Icon d={<><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 6l9 7 9-7"/></>}/>,
  phone:     <Icon d="M5 4h4l2 5-3 2a12 12 0 0 0 6 6l2-3 5 2v4a2 2 0 0 1-2 2A17 17 0 0 1 3 6a2 2 0 0 1 2-2z"/>,
  sparkle:   <Icon d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6z"/>,
  arrowR:    <Icon d="M5 12h14M13 6l6 6-6 6"/>,
  home:      <Icon d={<><path d="M3 11l9-7 9 7"/><path d="M5 10v10h14V10"/></>}/>,
  grid:      <Icon d={<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>}/>,
  table:     <Icon d={<><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M3 16h18M9 4v16M15 4v16"/></>}/>,
  reports:   <Icon d={<><path d="M4 4v16h16"/><rect x="7.5" y="11" width="2.6" height="6" rx="0.6"/><rect x="12" y="7.5" width="2.6" height="9.5" rx="0.6"/><rect x="16.5" y="13.5" width="2.6" height="3.5" rx="0.6"/></>}/>,
  sliders:   <Icon d={<><path d="M4 7h10M18 7h2M4 17h2M10 17h10"/><circle cx="16" cy="7" r="2.2"/><circle cx="8" cy="17" r="2.2"/></>}/>,
  columns:   <Icon d={<><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16M15 4v16"/></>}/>,
  shield:    <Icon d={<><path d="M12 3l7 3v6c0 4.6-3 7.8-7 9-4-1.2-7-4.4-7-9V6z"/><path d="M9 12l2 2 4-4"/></>}/>,
  key:       <Icon d={<><circle cx="8" cy="15" r="4"/><path d="M11 12l8-8M16 4l3 0 0 3M14 9l2 2"/></>}/>,
  microsoft: <Icon d={<><rect x="3" y="3" width="8" height="8" fill="currentColor" stroke="none"/><rect x="13" y="3" width="8" height="8" fill="currentColor" stroke="none"/><rect x="3" y="13" width="8" height="8" fill="currentColor" stroke="none"/><rect x="13" y="13" width="8" height="8" fill="currentColor" stroke="none"/></>}/>,
} satisfies Record<string, IconElement>;

export type IconName = keyof typeof ic;
